import React, { useMemo } from 'react';
import { useTheme } from '../../contexts/ThemeContext';

// ---------------------------------------------------------------------------
// Volume Discount Tiers (must match api-shipments/routes/shipment.js)
// ---------------------------------------------------------------------------
const TIERS = [
    { threshold: 1000, discountPercent: 1 },
    { threshold: 2000, discountPercent: 2 },
    { threshold: 3000, discountPercent: 3 },
];
const MAX_DISCOUNT = 3;
const MAX_TIER_THRESHOLD = 3000;

const computeVolumeStats = monthlySpend => {
    // Current discount
    const activeTier = [...TIERS].reverse().find(t => monthlySpend >= t.threshold);
    const discountPercent = activeTier ? activeTier.discountPercent : 0;

    // Next tier
    const nextTier = TIERS.find(t => monthlySpend < t.threshold);
    const nextThreshold = nextTier ? nextTier.threshold : null;
    const nextDiscountPercent = nextTier ? nextTier.discountPercent : null;
    const amountToNext = nextThreshold !== null ? Math.max(0, nextThreshold - monthlySpend) : 0;

    // Progress bar spans the full $0 → MAX_TIER_THRESHOLD range
    const progressPct = Math.min(100, (monthlySpend / MAX_TIER_THRESHOLD) * 100);

    return { discountPercent, nextThreshold, nextDiscountPercent, amountToNext, progressPct };
};

/**
 * Compute current-month spend from an array of shipment objects.
 * Accepts the combined completed + active shipments from the merchant dashboard.
 */
export const computeMonthlySpendFromShipments = shipments => {
    if (!Array.isArray(shipments) || shipments.length === 0) return 0;
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
    const completedStatuses = new Set(['shipment_completed', 'shipment_pending_review']);

    return shipments.reduce((sum, s) => {
        if (!s) return sum;
        if (!completedStatuses.has(s.status)) return sum;
        // Mirror the backend logic: use completed_at when present, fall back to
        // created_at. This ensures orders created in a prior month but completed
        // this month are counted correctly.
        const completedAt = s.completed_at ? new Date(s.completed_at).getTime() : 0;
        const createdAt = s.created_at ? new Date(s.created_at).getTime() : 0;
        const relevantDate = completedAt > 0 ? completedAt : createdAt;
        if (relevantDate < startOfMonth) return sum;
        return sum + Number(s.shipping_fee || 0);
    }, 0);
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const VolumeDiscountMeter = ({ monthlySpend = 0 }) => {
    const { isDarkMode } = useTheme();
    const stats = useMemo(() => computeVolumeStats(monthlySpend), [monthlySpend]);

    const { discountPercent, nextThreshold, nextDiscountPercent, amountToNext, progressPct } = stats;
    const atMax = discountPercent >= MAX_DISCOUNT;

    // Estimated savings this month: the discount shaves discountPercent percentage
    // points off the markup. A rough customer-facing savings estimate is
    // monthlySpend × discountPercent% (conservative — actual markup savings).
    const estimatedSavings = monthlySpend > 0 && discountPercent > 0
        ? monthlySpend * (discountPercent / 100)
        : 0;

    const containerStyle = {
        margin: '0 0 16px 0',
        padding: '14px 20px',
        borderRadius: '8px',
        background: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
        border: isDarkMode ? '1px solid rgba(255,255,255,0.10)' : '1px solid rgba(0,0,0,0.08)',
    };

    const labelStyle = {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '8px',
    };

    const titleStyle = {
        fontSize: '13px',
        fontWeight: 600,
        color: isDarkMode ? 'rgba(255,255,255,0.85)' : '#495057',
        margin: 0,
    };

    const badgeStyle = {
        fontSize: '12px',
        fontWeight: 700,
        padding: '3px 10px',
        borderRadius: '12px',
        background: discountPercent > 0 ? '#28a745' : (isDarkMode ? 'rgba(255,255,255,0.15)' : '#e9ecef'),
        color: discountPercent > 0 ? '#fff' : (isDarkMode ? 'rgba(255,255,255,0.6)' : '#6c757d'),
    };

    const trackStyle = {
        height: '8px',
        borderRadius: '4px',
        background: isDarkMode ? 'rgba(255,255,255,0.12)' : '#dee2e6',
        overflow: 'hidden',
        marginBottom: '6px',
    };

    const fillColor = discountPercent > 0 ? '#28a745' : '#f7941d';

    const fillStyle = {
        height: '100%',
        width: `${progressPct}%`,
        background: fillColor,
        borderRadius: '4px',
        transition: 'width 0.4s ease',
    };

    const subTextStyle = {
        fontSize: '11px',
        color: isDarkMode ? 'rgba(255,255,255,0.55)' : '#6c757d',
        margin: 0,
    };

    const savingsStyle = {
        fontSize: '11px',
        fontWeight: 600,
        color: '#28a745',
        margin: '4px 0 0 0',
    };

    // Tick marks positioned as % of the total bar width
    const tickMarks = TIERS.map(tier => ({
        ...tier,
        pct: (tier.threshold / MAX_TIER_THRESHOLD) * 100,
        reached: monthlySpend >= tier.threshold,
    }));

    let statusText;
    if (atMax) {
        statusText = `Maximum Discount ${MAX_DISCOUNT} active on all quotes this month!`;
    } else if (discountPercent > 0) {
        statusText = `Discount ${discountPercent} active — spend $${amountToNext.toFixed(0)} more this month to unlock Discount ${nextDiscountPercent}`;
    } else {
        statusText = `Spend $${amountToNext.toFixed(0)} more this month to unlock Discount ${nextDiscountPercent}`;
    }

    return (
        <div style={containerStyle}>
            <div style={labelStyle}>
                <span style={titleStyle}>Monthly Volume Discount</span>
                <span style={badgeStyle}>
                    {discountPercent > 0 ? `Discount ${discountPercent}` : 'No discount yet'}
                </span>
            </div>
            {/* Progress bar with tick marks for each tier */}
            {/* paddingTop creates room for the spend label that floats above the track */}
            <div style={{ position: 'relative', marginBottom: '18px', paddingTop: '22px' }}>
                <div style={trackStyle}>
                    <div style={fillStyle} />
                </div>
                {/* Tier marker lines + labels */}
                {tickMarks.map(tick => (
                    <div
                        key={tick.threshold}
                        style={{
                            position: 'absolute',
                            left: `${tick.pct}%`,
                            top: 0,
                            transform: 'translateX(-50%)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                        }}
                    >
                        {/* Tick line on the bar */}
                        <div style={{
                            width: '2px',
                            height: '8px',
                            background: tick.reached
                                ? '#28a745'
                                : (isDarkMode ? 'rgba(255,255,255,0.3)' : '#ced4da'),
                        }} />
                        {/* Label below */}
                        <span style={{
                            fontSize: '9px',
                            marginTop: '3px',
                            whiteSpace: 'nowrap',
                            color: tick.reached
                                ? '#28a745'
                                : (isDarkMode ? 'rgba(255,255,255,0.4)' : '#adb5bd'),
                            fontWeight: tick.reached ? 600 : 400,
                        }}>
                            ${tick.threshold.toLocaleString()}<br />D{tick.discountPercent}
                        </span>
                    </div>
                ))}
                {/* Current spend label — always visible, pinned above the fill tip */}
                {monthlySpend < MAX_TIER_THRESHOLD && (
                    <div style={{
                        position: 'absolute',
                        left: `${Math.max(2, Math.min(progressPct, 94))}%`,
                        top: '2px',
                        transform: progressPct < 5 ? 'translateX(0)' : 'translateX(-50%)',
                        fontSize: '10px',
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                        color: discountPercent > 0 ? '#28a745' : '#f7941d',
                        background: isDarkMode ? 'rgba(39,41,61,0.9)' : 'rgba(255,255,255,0.9)',
                        padding: '1px 5px',
                        borderRadius: '4px',
                        border: `1px solid ${discountPercent > 0 ? '#28a745' : '#f7941d'}`,
                        lineHeight: '1.4',
                    }}>
                        ${monthlySpend.toFixed(2)}
                    </div>
                )}
            </div>
            <p style={{ ...subTextStyle, marginTop: '4px' }}>
                {statusText}
            </p>
            {estimatedSavings > 0 && (
                <p style={savingsStyle}>
                    ~${estimatedSavings.toFixed(2)} saved this month on your deliveries
                </p>
            )}
        </div>
    );
};

export default VolumeDiscountMeter;
